import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import App from './App';
import { AuthProvider } from './context/auth';

import './global.css';

import * as Sentry from '@sentry/react';
import isDebug from './utils/isDebug';
import { scrubSentryBreadcrumb, scrubUrlParams } from './utils/scrubSensitiveParams';
import DocumentProvider from './providers/DocumentProvider/DocumentProvider';
import components from './providers/DocumentProvider/components';
import { ChakraProvider } from '@chakra-ui/react';
import { theme } from './theme';

Sentry.init({
  enabled: !isDebug,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration(), Sentry.browserProfilingIntegration()],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost'], // import.meta.env.VITE_API_BASE_URL
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  release: `${import.meta.env.PACKAGE_VERSION}-${import.meta.env.VITE_GIT_COMMIT}`,
  environment: `${import.meta.env.VITE_SENTRY_ENV}`,
  // Auth tokens travel in URLs — verifyEmailToken in a query string, the
  // password-setup token as a path segment — and Sentry records both request URLs
  // (as breadcrumbs) and the page URL on the event. Neither hook existed here.
  beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
  beforeSend: (event) => {
    if (event.request?.url) event.request.url = scrubUrlParams(event.request.url);

    return event;
  },
  // beforeSend only runs for *error* events. BrowserTracing names page-load and
  // navigation transactions from window.location.pathname, so a token route like
  // /password-setup/<token> reaches Sentry as a transaction name and as span
  // attributes without these two hooks.
  beforeSendTransaction: (event) => {
    if (event.transaction) event.transaction = scrubUrlParams(event.transaction);
    if (event.request?.url) event.request.url = scrubUrlParams(event.request.url);

    return event;
  },
  beforeSendSpan: (span) => {
    if (span.description) span.description = scrubUrlParams(span.description);

    return span;
  },
});

const rootElement = document.getElementById('root')!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      {/*
        ChakraProvider sits above AuthProvider, not inside App. AuthProvider is
        about to gate the first render on a session check and render LoadingPage
        while it resolves — and LoadingPage is built from Chakra primitives, so
        from inside App it would render outside its own provider. The router's
        defaultPendingComponent does not cover this either: the router is not
        mounted yet, that fallback is for route transitions.
      */}
      <ChakraProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <DocumentProvider components={components}>
            <AuthProvider>
              <App />
            </AuthProvider>
          </DocumentProvider>
        </QueryClientProvider>
      </ChakraProvider>
    </StrictMode>,
  );
}
